// The change queue reduced to what the Storage & Sync page, the offline bar and the sidebar show
// (BL-2685): is anything waiting, and what failed. One derivation feeds all three, so the sidebar's
// red count equals the failure rows listed. Reads the phone's JS queue and the native queue (ADR 066).
import type { ChangeQueueGroupView, ChangeQueueRowView } from './change-queue-display';
import type { LotConflictField } from './lot-desired-state';
import { failureReasonText, isUncurableByRetry } from './failure-reason';

/** What a failure row offers first: send again, look at a conflict, or (a refused change) discard. */
export type FailureAction = 'retry' | 'resolve' | 'discard';

/** One failed item, named, with its reason and its action. */
export interface FailureItem {
  /** Row key: unique among failures. */
  key: string;
  source: 'js' | 'native';
  /** The lot, photo or video, e.g. "Add 1 document · Lot 7 · Tractor". */
  name: string;
  reason: string;
  action: FailureAction;
  /** True when a confirm-gated Discard is offered too. */
  canDiscard: boolean;
  /** The entry (js) or rows (native) the actions name. */
  ids: string[];
  /** A conflict's fields with the phone's and the server's value; null otherwise. */
  conflict: LotConflictField[] | null;
}

/** The native queue as the derivation reads it. */
export interface NativeQueueInput {
  summary: { pending: number; inFlight: number; retrying: number; failed: number; pausedForSignIn: boolean };
  failedRows: { id: string; label: string; errorCode: string | null }[];
}

/** What {@link deriveQueueStatus} returns. */
export interface QueueStatus {
  /** True while anything is queued and not failed. */
  pending: boolean;
  failures: FailureItem[];
  /** The failure count the sidebar shows: always `failures.length`. */
  issues: number;
}

function jsFailure(row: ChangeQueueRowView, heading: string | null): FailureItem {
  const action: FailureAction = row.actions.includes('overwrite') ? 'resolve' : row.actions.includes('retry') ? 'retry' : 'discard';
  return {
    key: row.key,
    source: 'js',
    name: heading == null ? row.title : `${row.title} · ${heading}`,
    reason: row.reason ?? failureReasonText(null),
    action,
    canDiscard: row.actions.includes('discard'),
    ids: [row.actionEntryId],
    conflict: row.conflict,
  };
}

const isWaiting = (row: ChangeQueueRowView) => row.status === 'not-synced' || row.status === 'syncing';

/**
 * @param input.groups - the JS queue's groups from `buildChangeQueueGroups`.
 * @param input.native - the native queue's summary and failed rows, or null when there is none.
 * @param input.onUnknownCode - called with a native failure code that has no wording yet.
 * @returns whether anything waits, and the failures to list.
 */
export function deriveQueueStatus(input: {
  groups: readonly ChangeQueueGroupView[];
  native: NativeQueueInput | null;
  onUnknownCode?: (code: string | null) => void;
}): QueueStatus {
  const { groups, native, onUnknownCode } = input;
  const failures: FailureItem[] = [];
  let pending = false;
  for (const group of groups) {
    for (const row of group.rows) {
      if (isWaiting(row)) pending = true;
      if (row.actions.length > 0) failures.push(jsFailure(row, group.heading));
      for (const item of row.failedItems) failures.push(jsFailure(item, group.heading));
    }
  }
  if (native != null) {
    const { summary } = native;
    if (summary.pending + summary.inFlight + summary.retrying > 0 || summary.pausedForSignIn) pending = true;
    for (const failed of native.failedRows) {
      failures.push({
        key: failed.id,
        source: 'native',
        name: failed.label,
        reason: failureReasonText(failed.errorCode, onUnknownCode),
        action: 'retry',
        canDiscard: isUncurableByRetry(failed.errorCode),
        ids: [failed.id],
        conflict: null,
      });
    }
  }
  return { pending, failures, issues: failures.length };
}
